/**
 * Zod i18n plugin (zod v4)
 *
 * Explicitly maps the active @nuxtjs/i18n locale to zod error translations:
 *   pt-PT  →  i18n/locales/pt-br.json  →  zod.errors.*  (Portuguese)
 *   en-GB  →  i18n/locales/en.json     →  zod.errors.*  (English)
 *
 * The error map is (re-)applied on every locale switch so validation messages
 * always reflect the user's current language.
 */
import { z } from 'zod'
import type { ZodErrorMap } from 'zod'

export default defineNuxtPlugin({
  name: 'zod-i18n',
  setup(nuxtApp) {
    const i18n = nuxtApp.$i18n as ReturnType<typeof useI18n>
    const { t, locale } = i18n

    const buildErrorMap = (): ZodErrorMap => ((issue: any) => {
      let message: string | undefined

      switch (issue.code) {
        // ── Type mismatch ───────────────────────────────────────────────────
        case z.ZodIssueCode.invalid_type: {
          const received = issue.input === undefined ? 'undefined' : issue.input === null ? 'null' : typeof issue.input
          if (received === 'undefined' || received === 'null') {
            message = t('zod.errors.required')
          } else {
            message = t('zod.errors.invalid_type', {
              expected: issue.expected,
              received: received ?? '?',
            })
          }
          break
        }

        // ── Format violations (email, url, uuid, ip, regex, …) ─────────────
        // Replaces v3 invalid_string
        case z.ZodIssueCode.invalid_format: {
          const fmt = (issue as any).format as string
          const key = `zod.errors.invalid_format_${fmt}`
          const translated = t(key)
          if (translated !== key) {
            message = translated
          } else {
            // startsWith / endsWith carry extra params
            if (fmt === 'starts_with') {
              message = t('zod.errors.invalid_format_starts_with', { value: (issue as any).prefix })
            } else if (fmt === 'ends_with') {
              message = t('zod.errors.invalid_format_ends_with', { value: (issue as any).suffix })
            }
          }
          break
        }

        // ── Value not in allowed set (enum / literal) ───────────────────────
        // Replaces v3 invalid_enum_value + invalid_literal
        case z.ZodIssueCode.invalid_value: {
          const values = (issue as any).values as unknown[]
          message = t('zod.errors.invalid_value', {
            values: values.map(v => JSON.stringify(v)).join(' | '),
          })
          break
        }

        // ── Unrecognized object keys ────────────────────────────────────────
        case z.ZodIssueCode.unrecognized_keys: {
          message = t('zod.errors.unrecognized_keys', {
            keys: issue.keys.join(', '),
          })
          break
        }

        // ── Union / discriminated union ─────────────────────────────────────
        case z.ZodIssueCode.invalid_union: {
          message = t('zod.errors.invalid_input')
          break
        }

        // ── Size constraints (string, number, array, set, date, …) ─────────
        case z.ZodIssueCode.too_small: {
          const { origin, minimum, exact, inclusive } = issue as any
          const sizeKey = exact ? 'exact' : inclusive ? 'inclusive' : 'not_inclusive'
          const key = `zod.errors.too_small_${origin}_${sizeKey}`
          const translated = t(key, {
            minimum: typeof minimum === 'bigint' ? Number(minimum) : minimum,
          })
          if (translated !== key) {
            message = translated
          }
          break
        }

        case z.ZodIssueCode.too_big: {
          const { origin, maximum, exact, inclusive } = issue as any
          const sizeKey = exact ? 'exact' : inclusive ? 'inclusive' : 'not_inclusive'
          const key = `zod.errors.too_big_${origin}_${sizeKey}`
          const translated = t(key, {
            maximum: typeof maximum === 'bigint' ? Number(maximum) : maximum,
          })
          if (translated !== key) {
            message = translated
          }
          break
        }

        // ── Numeric constraints ─────────────────────────────────────────────
        case z.ZodIssueCode.not_multiple_of: {
          message = t('zod.errors.not_multiple_of', {
            divisor: String((issue as any).divisor),
          })
          break
        }

        // ── Invalid map key / array element ────────────────────────────────
        case z.ZodIssueCode.invalid_key:
        case z.ZodIssueCode.invalid_element: {
          message = t('zod.errors.invalid_input')
          break
        }

        // ── Custom errors ───────────────────────────────────────────────────
        case z.ZodIssueCode.custom: {
          // Allow schemas to pass an i18n key via params.i18n
          if (issue.params?.i18n) {
            message = t(`zod.errors.${issue.params.i18n}`)
          }
          break
        }
      }

      return { message: message ?? issue.message ?? t('zod.errors.invalid_input') }
    }) as ZodErrorMap

    // Apply on boot and on every locale switch (pt-PT ↔ en-GB)
    z.setErrorMap(buildErrorMap())
    watch(locale, () => z.setErrorMap(buildErrorMap()))
  },
})
